import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { ArrowBackRounded, CheckCircleRounded } from "@mui/icons-material";
import { UserAuth } from "./AuthProvider";
import { b2cTechyrrDomains, BASE_URL } from "lib/utilities/globalConstants";
import { tutorialSteps } from "../lib/utilities/tutorialSteps.js";
import { tutorialMessages } from "../lib/utilities/tutorialMessages.js";

const TutorialContext = createContext();

const TutorialProvider = ({ children }) => {
  const [tutorialStep, setTutorialStep] = useState(1);
  const [pendingStep, setPendingStep] = useState(null);
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [highlightRect, setHighlightRect] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [tutorialCompleted, setTutorialCompleted] = useState(true);

  const { user, initializing } = UserAuth();
  const currentStepRef = useRef(null);
  const animationFrameRef = useRef(null);

  const fetchTutorialStatus = async () => {
    const isTechyrr = b2cTechyrrDomains.includes(window.location.host);
    if (!user || isTechyrr) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}admin/getTutorialAdmin`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setTutorialCompleted(data.data.tutorialCompleted);
        if (!data.data.tutorialCompleted && window.location.pathname === "/admin/dashboard") {
          setPendingStep(1);
          setIsTutorialActive(true);
        }
      } else {
        throw new Error(data.message || "Failed to check tutorial status");
      }
    } catch (error) {
      setTutorialCompleted(true);
    }
  };

  const updateTutorialStatus = async () => {
    if (!user) return;

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}admin/updateTutorialAdmin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setTutorialCompleted(true);
        setIsTutorialActive(false);
        setTooltipVisible(false);
        setHighlightRect(null);
        setPendingStep(null);
        setTutorialStep(tutorialSteps.SUBMIT_BUTTON);
      } else {
        throw new Error(data.message || "Failed to update tutorial status");
      }
    } catch (error) {}
  };

  const scrollToStepElement = (step) => {
    const stepElement = document.querySelector(`[data-step="${step}"]`);
    if (stepElement) {
      stepElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  };

  const nextStep = () => {
    if (tutorialCompleted || !isTutorialActive) {
      return;
    }

    if (tutorialStep < tutorialSteps.SUBMIT_BUTTON) {
      setHighlightRect(null);
      setTooltipVisible(false);
      setPendingStep(tutorialStep + 1);
      scrollToStepElement(tutorialStep + 1);
    } else if (tutorialStep === tutorialSteps.SUBMIT_BUTTON) {
      finishTutorial();
    }
  };

  const prevStep = () => {
    if (tutorialCompleted || !isTutorialActive) {
      return;
    }

    if (tutorialStep > 1) {
      setTooltipVisible(false);
      setHighlightRect(null);
      setPendingStep(tutorialStep - 1);
      scrollToStepElement(tutorialStep - 1);
    }
  };

  const skipTutorial = () => {
    if (tutorialCompleted || !isTutorialActive) {
      return;
    }

    const confirmSkip = window.confirm("Are you sure you want to skip the tutorial?");
    if (confirmSkip) {
      updateTutorialStatus();
    }
  };

  const finishTutorial = () => {
    if (tutorialCompleted || !isTutorialActive) {
      return;
    }

    updateTutorialStatus();
  };

  const resetTutorial = () => {
    setTutorialStep(1);
    setPendingStep(1);
    setTooltipVisible(false);
    setHighlightRect(null);
    setTutorialCompleted(false);
    setIsTutorialActive(true);
  };

  const attemptToShowInfoBox = (step) => {
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      const stepElement = document.querySelector(`[data-step="${step}"]`);
      if (stepElement) {
        clearInterval(interval);
        showTooltip(step);
        setPendingStep(null);
        setTutorialStep(step);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        if (tutorialMessages[step]) {
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          const zoomLevel = window.devicePixelRatio || 1;
          setTooltipContent(tutorialMessages[step]);
          setTooltipPosition({
            top: (viewportHeight - tutorialMessages[step].positionConfig.tooltipHeight / zoomLevel) / 2,
            left: (viewportWidth - tutorialMessages[step].positionConfig.tooltipWidth / zoomLevel) / 2,
          });
          setTooltipVisible(true);
          setHighlightRect(null);
        }
        setPendingStep(null);
      } else {
        attempts++;
      }
    }, 200);

    return () => clearInterval(interval);
  };

  const showTooltip = (step) => {
    const stepElement = document.querySelector(`[data-step="${step}"]`);
    const message = tutorialMessages[step];

    if (!message) {
      setTooltipVisible(false);
      setHighlightRect(null);
      return;
    }

    if (stepElement) {
      stepElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }

    const { positionConfig } = message;
    currentStepRef.current = stepElement;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const zoomLevel = window.devicePixelRatio || 1;
    let tooltipTop, tooltipLeft;

    // Scale tooltip dimensions and offsets based on zoom level
    const scaledTooltipWidth = positionConfig.tooltipWidth / zoomLevel;
    const scaledTooltipHeight = positionConfig.tooltipHeight / zoomLevel;
    const scaledOffsetX = (positionConfig.offsetX || 0) / zoomLevel;
    const scaledOffsetY = (positionConfig.offsetY || 0) / zoomLevel;

    if (stepElement) {
      const rect = stepElement.getBoundingClientRect();

      if (positionConfig.type === "below") {
        tooltipTop = rect.top + rect.height + scaledOffsetY;
        tooltipLeft = rect.left;
      } else if (positionConfig.type === "left-align") {
        tooltipTop = rect.top + rect.height + scaledOffsetY;
        tooltipLeft = rect.left;
      } else if (positionConfig.type === "left") {
        tooltipTop = rect.top;
        tooltipLeft = rect.left + scaledOffsetX;
      }

      if (tooltipLeft + scaledTooltipWidth > viewportWidth) {
        tooltipLeft = viewportWidth - scaledTooltipWidth - 10 / zoomLevel;
      }
      if (tooltipLeft < 0) {
        tooltipLeft = 240 / zoomLevel;
      }
      if (tooltipTop + scaledTooltipHeight > viewportHeight) {
        tooltipTop = rect.top - scaledTooltipHeight - 10 / zoomLevel;
      }

      setHighlightRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    } else {
      tooltipTop = (viewportHeight - scaledTooltipHeight) / 2;
      tooltipLeft = (viewportWidth - scaledTooltipWidth) / 2;
      setHighlightRect(null);
    }

    setTooltipPosition({
      top: tooltipTop,
      left: tooltipLeft,
    });

    setTooltipContent({
      ...message,
      positionConfig: {
        ...positionConfig,
        tooltipWidth: scaledTooltipWidth,
        tooltipHeight: scaledTooltipHeight,
      },
    });
    setTooltipVisible(true);
  };

  const adjustHighlightHeight = (step, heightOffset) => {
    const stepElement = document.querySelector(`[data-step="${step}"]`);
    if (stepElement) {
      const rect = stepElement.getBoundingClientRect();
      const dropdown = document.querySelector(".MuiPopover-root .MuiPaper-root");
      let adjustedHeight = rect.height;
      if (dropdown) {
        const dropdownRect = dropdown.getBoundingClientRect();
        adjustedHeight = Math.max(
          rect.height,
          dropdownRect.height + rect.height + (heightOffset || 0)
        );
      } else {
        adjustedHeight = rect.height + (heightOffset || 0);
      }

      setHighlightRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: adjustedHeight,
      });
    }
  };

  useEffect(() => {
    if (!initializing) {
      fetchTutorialStatus();
      setMounted(true);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [initializing]);

  useEffect(() => {
    if (mounted && !tutorialCompleted && pendingStep) {
      attemptToShowInfoBox(pendingStep);
    }
  }, [pendingStep, mounted, tutorialCompleted]);

  useEffect(() => {
    if (tutorialCompleted || !isTutorialActive) {
      setTooltipVisible(false);
      setPendingStep(null);
      setIsTutorialActive(false);
      return;
    }

    const handleClick = (e) => {
      const currentStepElement = document.querySelector(`[data-step="${tutorialStep}"]`);
      const tooltipBox = document.querySelector(".tutorial-box");
      const muiPopover = document.querySelector(".MuiPopover-root");

      const clickedInsideStep = currentStepElement?.contains(e.target);
      const clickedInsideTooltip = tooltipBox?.contains(e.target);
      const clickedInsideDropdown = muiPopover?.contains(e.target);

      if (!clickedInsideStep && !clickedInsideTooltip && !clickedInsideDropdown) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (clickedInsideStep && tutorialStep === tutorialSteps.SUBMIT_BUTTON) {
        finishTutorial();
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [tutorialStep, tutorialCompleted, isTutorialActive]);

  useEffect(() => {
    if (
      !tooltipVisible ||
      !currentStepRef.current
    ) {
      return;
    }

    const updatePosition = () => {
      const stepElement = currentStepRef.current;
      if (!stepElement) return;

      const message = tutorialMessages[tutorialStep];
      if (!message || !message.positionConfig) return;

      const { positionConfig } = message;
      const rect = stepElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const zoomLevel = window.devicePixelRatio || 1;

      const dropdown = document.querySelector(".MuiPopover-root .MuiPaper-root");
      let adjustedHeight = rect.height;
      if (dropdown) {
        const dropdownRect = dropdown.getBoundingClientRect();
        adjustedHeight = Math.max(rect.height, dropdownRect.height + rect.height);
      }

      // Scale tooltip dimensions and offsets
      const scaledTooltipWidth = positionConfig.tooltipWidth / zoomLevel;
      const scaledTooltipHeight = positionConfig.tooltipHeight / zoomLevel;
      const scaledOffsetX = (positionConfig.offsetX || 0) / zoomLevel;
      const scaledOffsetY = (positionConfig.offsetY || 0) / zoomLevel;

      let tooltipTop, tooltipLeft;

      if (positionConfig.type === "below") {
        tooltipTop = rect.top + rect.height + scaledOffsetY;
        tooltipLeft = rect.left;
      } else if (positionConfig.type === "left-align") {
        tooltipTop = rect.top + rect.height + scaledOffsetY;
        tooltipLeft = rect.left;
      } else if (positionConfig.type === "left") {
        tooltipTop = rect.top;
        tooltipLeft = rect.left + scaledOffsetX;
      }

      if (tooltipLeft + scaledTooltipWidth > viewportWidth) {
        tooltipLeft = viewportWidth - scaledTooltipWidth - 10 / zoomLevel;
      }
      if (tooltipLeft < 0) {
        tooltipLeft = 240 / zoomLevel; 
      }
      if (tooltipTop + scaledTooltipHeight > viewportHeight) {
        tooltipTop = rect.top - scaledTooltipHeight - 10 / zoomLevel;
      }

      setTooltipPosition({
        top: tooltipTop,
        left: tooltipLeft,
      });

      setHighlightRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: adjustedHeight,
      });

      animationFrameRef.current = requestAnimationFrame(updatePosition);
    };

    animationFrameRef.current = requestAnimationFrame(updatePosition);

    const observer = new MutationObserver(() => {
      if (currentStepRef.current && !tutorialCompleted && isTutorialActive) {
        updatePosition();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      observer.disconnect();
    };
  }, [tooltipVisible, tutorialStep, tutorialCompleted, isTutorialActive]);

  useEffect(() => {
    const updateZoomLevel = () => {
      const zoomLevel = window.devicePixelRatio || 1;
      document.documentElement.style.setProperty('--zoom-level', zoomLevel);
    };

    updateZoomLevel();
    window.addEventListener('resize', updateZoomLevel);
    return () => window.removeEventListener('resize', updateZoomLevel);
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        tutorialStep,
        setTutorialStep,
        tutorialSteps,
        nextStep,
        prevStep,
        adjustHighlightHeight,
        resetTutorial,
        skipTutorial,
      }}
    >
      {children}

      {/* Overlay */}
      {tooltipVisible && (
        <div
          className="tutorial-overlay-screen fixed top-0 left-0 w-full h-full z-[999]"
          style={{ backgroundColor: highlightRect ? "transparent" : "rgba(0, 0, 0, 0.5)" }}
        >
          {highlightRect && (
            <div
              className="tutorial-overlay-hole absolute pointer-events-none"
              style={{
                top: `${highlightRect.top}px`,
                left: `${highlightRect.left}px`,
                width: `${highlightRect.width}px`,
                height: `${highlightRect.height}px`,
                border: "3px solid #3b82f6",
                boxShadow: "0 0 10px rgba(59, 130, 246, 0.5), 0 0 0 9999px rgba(0, 0, 0, 0.5)",
                borderRadius: "6px",
              }}
            />
          )}
        </div>
      )}

      {/* Tooltip */}
      {tooltipVisible && tooltipContent && (
        <div
          className={`tutorial-box ${tooltipVisible ? "visible" : ""} ${
            tutorialStep === 1 || tutorialStep === 18 ? "welcome-message" : ""
          } fixed z-[10000] bg-white rounded-lg p-4 shadow-lg pointer-events-auto`}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            maxWidth: `${tooltipContent.positionConfig.tooltipWidth}px`,
            maxHeight: `${tooltipContent.positionConfig.tooltipHeight}px`,
          }}
        >
          <div className="flex justify-between items-center mb-2">
            <p className="m-0 text-xl font-medium">{tooltipContent.text}</p>
          </div>
          <div className="flex justify-end gap-2">
            {tooltipContent.showPrev && (
              <button
                onClick={prevStep}
                className="tutorial-prev-button flex items-center justify-center py-1 px-3 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                <ArrowBackRounded style={{ width: "18px", height: "18px" }} />
                Previous
              </button>
            )}
            {tooltipContent.showNext && (
              <button
                onClick={nextStep}
                className="tutorial-next-button flex items-center justify-center py-1 px-3 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                Next
                <svg
                  className="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}
            {tooltipContent.showSkip && (
              <button
                onClick={skipTutorial}
                className="tutorial-skip-button px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Skip
              </button>
            )}
            {tooltipContent.showFinish && (
              <button
                onClick={finishTutorial}
                className="tutorial-finish-button flex items-center justify-center py-1 px-3 text-sm text-white bg-green-600 rounded hover:bg-green-700"
              >
                Finish
                <CheckCircleRounded style={{ width: "18px", height: "18px" }} />
              </button>
            )}
          </div>
        </div>
      )}
    </TutorialContext.Provider>
  );
};

// Custom hook to use tutorial context
const Tutorial = () => {
  return useContext(TutorialContext);
};

export { TutorialProvider, TutorialContext, Tutorial };